import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/ui/shadcn/utils';

const buttonVariants = cva(
  'tw:appearance-none tw:border-0 tw:bg-transparent tw:p-0 tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-md tw:text-base tw:font-normal tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-1 tw:focus-visible:ring-ring tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:shadow tw:hover:bg-primary-hover tw:active:bg-primary-active',
        destructive:
          'tw:bg-destructive tw:text-destructive-foreground tw:shadow-sm tw:hover:bg-destructive-hover tw:active:bg-destructive-active',
        outline:
          'tw:border tw:border-input tw:bg-background tw:shadow-sm tw:hover:bg-accent tw:hover:text-accent-foreground',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:shadow-sm tw:hover:bg-secondary/80',
        neutral:
          'tw:bg-background tw:text-foreground tw:shadow-heavy tw:hover:bg-muted',
        ghost: 'tw:hover:bg-accent tw:hover:text-accent-foreground',
        link: 'tw:text-primary tw:underline tw:hover:text-primary-hover',
        success:
          'tw:bg-success tw:text-success-foreground tw:shadow tw:hover:bg-success-hover tw:active:bg-success-active',
        'secondary-toggle':
          'tw:border tw:border-border tw:bg-transparent tw:text-foreground tw:shadow-none tw:hover:border-success tw:hover:text-success',
        unstyled: 'tw:bg-transparent tw:text-inherit tw:shadow-none',
        'link-danger':
          'tw:text-destructive tw:underline tw:hover:text-destructive-hover',
        outlined:
          'tw:border tw:border-primary tw:bg-transparent tw:text-primary tw:shadow-none tw:hover:bg-primary/10',
        'outlined-danger':
          'tw:border tw:border-destructive tw:bg-transparent tw:text-destructive tw:shadow-none tw:hover:bg-destructive/10',
        icon: 'tw:relative tw:rounded-full tw:bg-transparent tw:shadow-none tw:hover:bg-canvas tw:active:bg-border tw:[&_svg]:absolute tw:[&_svg]:top-1/2 tw:[&_svg]:left-1/2 tw:[&_svg]:-translate-x-1/2 tw:[&_svg]:-translate-y-1/2',
      },
      size: {
        // h-10 on all three matches legacy's single global button height
        default: 'tw:h-10 tw:px-4 tw:py-2',
        sm: 'tw:h-10 tw:rounded-md tw:px-3 tw:text-xs',
        lg: 'tw:h-10 tw:rounded-md tw:px-8',
        icon: 'tw:h-9 tw:w-9',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'secondary-toggle',
        active: true,
        class: 'tw:border-success tw:bg-success-muted tw:text-success',
      },
      {
        variant: 'neutral',
        active: true,
        class: 'tw:bg-muted',
      },
      {
        variant: 'unstyled',
        class: 'tw:h-auto tw:w-auto tw:rounded-none tw:p-0',
      },
      {
        variant: ['link', 'link-danger'],
        class: 'tw:h-auto tw:w-auto tw:p-0',
      },
      {
        variant: 'icon',
        class: 'tw:h-auto tw:w-auto tw:p-3',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      active: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, active, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, active, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
